import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import { Alert, AlertTitle, GridLegacy } from "@mui/material";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Popper from "@mui/material/Popper";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import NotificationsIcon from "@mui/icons-material/NotificationsOutlined";
import InputIcon from "@mui/icons-material/Input";
import {
  AxiosRequest,
  createCokies,
  deleteURLparam,
  getLastLogin,
  getOptionsForUser,
  getURLparam,
  hasURLparam,
  LkTypes,
  pingServer,
  updateOptionsForUser,
  XMLrequest,
} from "../../../Url";
import clsx from "clsx";
import { getEsiaUrls, isEmptyObject } from "../../Tools/Tools";
import { toast } from "react-toastify";
import MarkChatReadIcon from "@mui/icons-material/MarkChatRead";
import CheckIcon from "@mui/icons-material/Check";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import Link from "@mui/material/Link";
import UserProfile from "./UserProfile";
import {
  RemoveAllBG,
  renderMessageBox,
  shellExecute,
  tokenProcessingTest,
} from "../../../TokenProcessing/TokenProcessing";
import CountDown from "./CountDown";
import { getUrlSearh } from "../../Tools/urlParams";
import { color, fontWeight, SxProps, Theme } from "@mui/system";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  appConfig,
  currentTheme,
  headFormAtom,
  hideToastNotificationsAtom,
  isAuthorized,
  resetWorkAreaAtom,
  smetaStore,
} from "../../../../atoms";
import SupportButton from "./SupportButton";
import smetaAxios from "../../../SmetaAxios";
import { AxiosRequestConfig } from "axios";
import { Button as DlgButton, Prefixes } from "../../Tools/ProjectX";
import { smetaModals } from "../../Tools/modalManager";
import { useNavigate } from "react-router";
import { requestLeaveAtom, useLeaveBeforeUnload } from "../../Tools/leaveGuard";
import {
  appNotificationsAtom,
  appUnreadNotificationCountAtom,
  clearAppNotificationsAtom,
  removeAppNotificationAtom,
  upsertAppNotificationAtom,
} from "../../notifications/appNotificationAtoms";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import styles from "./Header.module.css";
import { restoreSessionAfterIdle } from "../../../sessionRecovery";

// Статический импорт держал FormsMainFile (и всё за ним: jodit, графики) в стартовой загрузке,
// из-за чего lazy для него в FullRightSideData не выделял отдельный чанк
// eslint-disable-next-line @typescript-eslint/naming-convention
const FormsMainFile = lazy(() => import("../../Sections/ElementsSections/Forms/FormsMainFile"));

const stmjsonUrl = async () => {
  const hasStmJson = hasURLparam("stmjson");
  if (hasStmJson) {
    const command = parseCommand(atob(getURLparam("stmjson")));
    const ablResp = await execABLProgram({ command });
    tokenProcessingTest(ablResp);
    deleteURLparam("stmjson");
  }
};

interface AblCommand {
  command: string;
  token?: string;
  module: string;
  params: object;
}

interface ExecuteABLProgram {
  command: AblCommand | null;
  id?: string;
  postData?: unknown;
}

type YanaoLogoutResponse = {
  ok?: boolean;
  status?: string | number;
  message?: string;
};

const YANAO_ESIA_CLIENT_ID = 9999;
const YANAO_ESIA_LOGOUT_URL = "https://esia.gosuslugi.ru/idp/ext/Logout";
const YANAO_ESIA_REDIRECT_URL = "https://smeta.yanao.ru";
const YANAO_ESIA_LOGOUT_REDIRECT_DELAY = 300;

function getYanaoLogoutErrorMessage(error: unknown) {
  if (error && typeof error === "object" && "response" in error) {
    const response = (error as { response?: { data?: { message?: string }; status?: number } }).response;
    if (response?.data?.message) return response.data.message;
    if (response?.status === 401) return "Сессия не найдена или истекла";
    if (response?.status === 403) return "Доступ запрещён";
    if (response?.status) return `Ошибка сервера: ${response.status}`;
  }
  if (error instanceof Error) return error.message;
  return "не удалось завершить сессию";
}

function renderYanaoLogoutError(message: string) {
  renderMessageBox({
    dlgType: 1,
    buttons: 1,
    message: `Ошибка выхода: ${message}`,
  });
}

function isYanaoLogoutSuccess(resp: YanaoLogoutResponse) {
  return resp.ok === true || resp.status === "logged_out";
}

function getYanaoLogoutResponseError(resp: YanaoLogoutResponse) {
  if (resp.message) return resp.message;
  if (resp.status === 401) return "Сессия не найдена или истекла";
  if (resp.status === 403) return "Доступ запрещён";
  if (typeof resp.status === "number") return `Ошибка сервера: ${resp.status}`;
  return "сервер не подтвердил завершение сессии";
}

function buildYanaoEsiaLogoutUrl() {
  const params = new URLSearchParams({
    client_id: String(YANAO_ESIA_CLIENT_ID),
    redirect_url: YANAO_ESIA_REDIRECT_URL,
  });
  return `${YANAO_ESIA_LOGOUT_URL}?${params.toString()}`;
}

function requestYanaoLogout() {
  return smetaAxios.post<YanaoLogoutResponse>(
    "",
    {},
    {
      baseURL: window.location.origin,
      prefix: "/logout" as Prefixes,
      disableLicGuid: true,
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
}

export const execABLProgram = async ({ command, id, postData }: ExecuteABLProgram) => {
  if (command === null || (command.module !== "programs" && command.module !== "tools"))
    return console.error("Ошибка запроса execABLProgram");
  const prefix = command.module === "programs" ? Prefixes.progs : Prefixes.tools;
  const config: AxiosRequestConfig = {
    prefix: prefix,
    method: isEmptyObject(postData) ? "GET" : "POST",
    url: "execABLProgram",
    data: postData,
    params: {
      ...command.params,
      Notification: id,
      WSM: 1,
    },
  };
  const json = await smetaAxios(config);
  tokenProcessingTest(json);
};

type ParsedData = {
  [key: string]: string | ParsedData;
};

function parseString(input: string): ParsedData {
  const result: ParsedData = {};

  // убираем stmjson в начале строки
  input = input.replace(/^stmjson:/, "");

  // разбиваем строку на части по запятым. но игнорируем запятые внутри params
  const parts = input.match(/(?:[^,{}]+|\{[^{}]*\})+/g);

  if (!parts) return result;

  for (const part of parts) {
    // разбираем параметр с ключом и значением
    const match = part.match(/^([^:={]+)[:=](.+)$/);
    if (match) {
      const key = match[1].trim();
      let value = match[2].trim();

      // если значение params то парсим его рекурсивно
      if (value.startsWith("{") && value.endsWith("}")) {
        value = value.slice(1, -1);
        result[key] = parseString(value);
      } else {
        result[key] = value.replace(/^"(.*)"$/, "$1"); // убираем кавычки
      }
    } else {
      if (!result["command"]) {
        result["command"] = part.trim();
      }
    }
  }

  return result;
}

export const parseCommand = (inputCommand: string): AblCommand | null => {
  try {
    const reg = /[A-zА-я0-9|?&\-_.\s]+/g;
    const checkJsonStyle = new RegExp("stmjson:[A-Za-z_]+:[A-Za-z_]");

    let result = inputCommand.replaceAll('"', "");
    result = result.replaceAll(`'`, "");

    if (result.match(checkJsonStyle)) {
      result = result.replaceAll("stmjson:", "stmjson:module,");
    } else {
      result = result.replaceAll("=", ":");
    }

    const set: any = new Set(result.match(reg));

    for (let el of set) {
      result = result.replaceAll(el, '"' + el + '"');
    }

    return JSON.parse("{" + result + "}");
  } catch {
    return parseString(inputCommand);
  }
};

const createTokenNavigate = (stmJson: AblCommand) => {
  tokenProcessingTest({
    token: "Navigate",
    Params: { PageID: stmJson.params.pageid || stmJson.params.PageID, RequestID: "0" },
  });
};

const stmJsonClient = (stmJson: AblCommand) => {
  const tokens = {
    navigate: createTokenNavigate,
  };
  return tokens[stmJson.command] || null;
};

const stmJsonRequest = (stmJson: AblCommand, id: string) => {
  return execABLProgram({ command: stmJson, id });
};

const stmJsonShell = (stmJson: AblCommand) => {
  const operations = {
    open: shellExecute,
    download: null,
  };
  if (operations[stmJson?.params.operation])
    operations[stmJson?.params.operation]({
      Params: { FileName: stmJson.params.filename, RCDATA: stmJson.params.rcdata },
    });
};

export const stmjsonClick = (stmJson: AblCommand, id?: string) => {
  //todo заменить HandleNotifClick
  const commands = {
    navigate: createTokenNavigate,
    client: stmJsonClient,
    request: stmJsonRequest,
    shell: stmJsonShell,
  };
  if (commands[stmJson.command]) commands[stmJson.command](stmJson, id);
};

export const getHeadForm = async (command: string) => {
  const {
    smeta: { homeOptions },
  } = smetaStore.get(appConfig);
  const config: AxiosRequestConfig = {
    prefix: Prefixes.form,
    loadMask: true,
    params: {
      height: (homeOptions?.header?.height || 38) + "px",
    },
  };
  const headData: { Head?: object } = await smetaAxios.get(command, config);
  return headData.Head ? headData.Head : headData;
};

const DashboardNavbar = (props: any) => {
  const config = useAtomValue(appConfig);
  const hideToastNotifications = useAtomValue(hideToastNotificationsAtom);
  const theme = useAtomValue(currentTheme);
  const headForm = useAtomValue(headFormAtom);
  const navigate = useNavigate();
  const [isSessionActive, setIsSessionActive] = useAtom(isAuthorized);
  const resetWorkArea = useSetAtom(resetWorkAreaAtom);
  const requestLeave = useSetAtom(requestLeaveAtom);
  const drx = useRef(getLastLogin().split(","));
  const notifications = useAtomValue(appNotificationsAtom);
  const unreadCount = useAtomValue(appUnreadNotificationCountAtom);
  const upsertNotification = useSetAtom(upsertAppNotificationAtom);
  const removeNotification = useSetAtom(removeAppNotificationAtom);
  const clearNotifications = useSetAtom(clearAppNotificationsAtom);
  const [showUnreadOnly, setShowUnreadOnly] = useState<boolean>();
  const [isOpen, setIsOpen] = useState(false);
  const [anim, setAnim] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  useLeaveBeforeUnload(() => {
    window.addEventListener("mousemove", mousemovehandler);
  });
  // const bc = new BroadcastChannel('test_channel');
  const updateNotificationsButton = (): SxProps<Theme> => {
    switch (theme) {
      case "light":
        return {
          color: "var(--font-color-light)",
        };
      case "blue":
        return {
          color: "var(--font-color-blue)",
          borderColor: "rgba(255, 255, 255, 0.5)",
          "&:hover": {
            borderColor: "rgb(255, 255, 255)",
            backgroundColor: "rgba(255, 255, 255, 0.04)",
          },
        };
      default:
        return {};
    }
  };

  // Первый ping после загрузки страницы сам восстанавливает подключение, переинициализация после него не нужна
  const startupPingDoneRef = useRef(false);

  const sessionCheck = async () => {
    if (isSessionActive === false) {
      backToLoginPage();
    } else {
      setTimeout(async () => {
        const pingJson = await pingServer(HandleNotification);
        startupPingDoneRef.current = true;
        if (!isEmptyObject(pingJson)) {
          Notifications(pingJson);
        }
      }, 100);
      setShowUnreadOnly(false);
    }
  };

  useEffect(() => {
    stmjsonUrl();
  }, []);

  useEffect(() => {
    sessionCheck();
  }, []);

  useEffect(() => {
    // bc.addEventListener('message', function (e) {
    // if (window.location.origin !== "http://localhost:3000")
    window.addEventListener("storage", StorageChange);
    return () => {
      window.removeEventListener("storage", StorageChange);
    };
  }, []);

  const StorageChange = (event: StorageEvent) => {
    if (event.newValue === "leave") {
      HandleSingOut();
    }
  };

  async function logoutYanaoEsia() {
    const resp = await requestYanaoLogout().catch((error) => {
      renderYanaoLogoutError(getYanaoLogoutErrorMessage(error));
      return null;
    });
    if (!resp) return false;

    if (!isYanaoLogoutSuccess(resp)) {
      renderYanaoLogoutError(getYanaoLogoutResponseError(resp));
      return false;
    }

    setTimeout(() => {
      window.location.href = buildYanaoEsiaLogoutUrl();
    }, YANAO_ESIA_LOGOUT_REDIRECT_DELAY);

    return true;
  }

  async function backToLoginPage() {
    clearNotifications();
    toast.dismiss();
    let navigateUrl = "/";
    setIsSessionActive(false);
    if (getOptionsForUser()?.authed_by === "esia" && config.lk.doEsia?.logout) {
      localStorage.setItem("login", "");
      if (config.lk.lkType === LkTypes.yanao) {
        await logoutYanaoEsia();
        return;
      }
      window.location.href = getEsiaUrls().logout;
    } else {
      localStorage.setItem("login", "");
      if (getOptionsForUser()?.from && getOptionsForUser()?.from !== "lk") {
        window.location.href = getOptionsForUser()?.from.replaceAll("@", "/");
      } else {
        if (getOptionsForUser()?.from && getOptionsForUser()?.from === "lk" && config.smeta.hideEnter === undefined) {
          navigate("/lk");
        } else {
          if (hasURLparam("parsed")) {
            navigateUrl += getUrlSearh();
          }
          navigate(navigateUrl);
        }
      }
    }
  }

  const mousemovehandler = () => {
    const licguid = sessionStorage.getItem("LicG");
    setTimeout(() => {
      if (licguid) localStorage.setItem("login", licguid);
    }, 3000);
    window.removeEventListener("mousemove", mousemovehandler);
  };

  const HandleNotification = (json: any) => {
    if (json !== undefined) {
      if (json.content !== undefined && json.content.substring(0, 40) === "Восстановлено подключение к конфигурации") {
        upsertNotification({
          id: "restoreConnetction",
          source: "system",
          read: false,
          data: {
            Time: `${new Date()}`,
            Title: "",
            Content: json.content,
            Options: 4,
          },
        });
        if (startupPingDoneRef.current) restoreSessionAfterIdle();
        toast(json.content, {
          type: "info",
          position: "bottom-right",
          style: { width: "350px" },
          onOpen: () => {
            setTimeout(() => {
              removeNotification("restoreConnetction");
            }, 1);
          },
          toastId: "restoreConnetction",
          data: {
            Time: `${new Date()}`,
            Title: "",
            Content: json.content,
            Options: 4,
          },
        });
      } else if (json.ID !== undefined) {
        RaiseToast(json);
      }
    }
  };

  const addNotification = () => {
    removeNotification("109203");
    toast.dismiss("109203");
    // const comand =
    //   'stmjson:module,command=request,module=programs,token=ExecABLProgram,params:{Name=ShowRequestDoc,params="ОД|4157498|Служба технадзора ЯНАО.drx"}';
    // let jsonCommandData = ParseCommand(comand);
    // RaiseToast({
    //   Options: 4,
    //   Command: jsonCommandData,
    //   Time: "11.01.2023 21:09:53",
    //   Title: "Title",
    //   content: "Открыть",
    //   ID: 1,
    // });
    // bc.postMessage('This is a test message.');
  };

  const toggleNotificationCenter = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    if (isOpen === true) {
      setTimeout(() => {
        setIsOpen(!isOpen);
      }, 499);
    } else {
      setIsOpen(!isOpen);
    }
    setAnim(!anim);
  };

  function HandleNotifications(comand: string, ID?: string | number) {
    let params = new Map();
    params.set("prefix", "audit");
    params.set("comand", "HandleNotifications");
    params.set("Command", comand);
    if (ID) params.set("ID", ID);
    params.set("smart", "1");
    return AxiosRequest(true, params);
  }

  function HandleNotificationClick(command: any, options: number, id: any) {
    let interval: NodeJS.Timeout;
    const setInterval = (int: NodeJS.Timeout) => {
      interval = int;
    };

    switch (command.command) {
      case "navigate":
        tokenProcessingTest({
          token: "Navigate",
          Params: { PageID: command.params.pageid, RequestID: "0" },
        });
        break;
      case "client":
        if (command.token === "exit") {
          const login = localStorage.getItem("login");
          if (login === "") {
            return;
          }
          smetaModals.open(
            <GridLegacy
              container
              direction="column"
              justifyContent="center"
              alignItems="center"
              sx={{ height: "100%" }}
            >
              <Typography>
                Через
                <CountDown
                  initialSeconds={30}
                  getInterval={setInterval}
                  onTimerEnd={HandleSingOut}
                  style={{ display: "inline" }}
                />{" "}
                произойдет автоматический выход из сессии. Продолжить работу?
              </Typography>
            </GridLegacy>,
            {
              Title: "Внимание",
              style: { height: "180px", width: "380px" },
              Buttons: {
                jsonData: {
                  Кнопка$Выбрать: "Выйти",
                  Кнопка$Отмена: "Остаться",
                },
                Params: { Buttons: DlgButton.Ok + DlgButton.Cancel },
              },
              onClose: () => {
                clearInterval(interval);
                const params = new Map();
                params.set("prefix", "project").set("comand", "info").set("info", "3");
                XMLrequest(params);
              },
              onSelect: () => {
                clearInterval(interval);
                HandleSingOut();
              },
              blockResize: true,
              blockMaximize: true,
            },
          );
        }
        if (command.token === "navigate")
          tokenProcessingTest({
            Token: "Navigate",
            Break: "1",
            Params: { PageID: `${command.params.PageID}`, RequestID: "0" },
          });
        break;
      case "request":
        execABLProgram({ command, id });
        break;
    }
    if (options & NotifyFlag_ImdClose) {
      markAsDone(id);
      setTimeout(() => {
        removeNotification(id);
      }, 500);
    }
  }

  const NotifyFlag_Immediately = 1; // выполнить команду сразу как получишь уведомление
  const NotifyFlag_CanClose = 2; // всплывающее уведомление и возможность закончить (закрыть) уведомление
  // const NotifyFlag_Balloon = 4; // всплывающее уведомление
  const NotifyFlag_ImdClose = 8; //всплывающее уведомление и после обработки уведомления, сразу его закрыть

  function Notifications(jsonFromPing?: any) {
    //  valueAny: any, options: number;
    HandleNotifications("list").then((json) => {
      if (!isEmptyObject(json) && json.Items) {
        json = json.Items.items;
        for (const value of Object.values(json)) {
          RaiseToast(value);
        }
      }
    });
  }

  async function finishAll() {
    for (const value of Object.values(notifications)) {
      if (value.source === "chat") {
        removeNotification(value.id);
        toast.dismiss(value.id);
        continue;
      }

      if (value.data && value.data.Options && value.data.Options & 2) {
        await markAsDone(value.id);
      }
    }
    Notifications();
  }

  function RaiseToast(value: any) {
    let jsonCommandData: any;
    try {
      if (value.Command) jsonCommandData = parseCommand(value.Command);
    } catch {}
    const options = Number(value.Options);
    upsertNotification({
      id: value.ID,
      source: "system",
      read: false,
      data: {
        Command: jsonCommandData,
        Time: value.Time,
        Title: value.Title,
        Content: value.content,
        Options: options,
      },
    });
    toast(`${value.Title}` + value.content === undefined ? "" : `${value.Title || ""}: ${value.content}`, {
      toastId: value.ID,
      type: "info",
      position: "bottom-right",
      style: {
        display: options & NotifyFlag_Immediately || isOpen === true ? "none" : "flex",
      },
      onClick: () => {
        HandleNotificationClick(jsonCommandData, options, value.ID);
      },
      data: {
        Command: jsonCommandData,
        Time: value.Time,
        Title: value.Title,
        Content: value.content,
        Options: options,
      },
    });
    if (options & NotifyFlag_Immediately) {
      HandleNotificationClick(jsonCommandData, options, value.ID);
    }
    if (hideToastNotifications) toast.dismiss();
  }

  async function markAsDone(id: string | number) {
    removeNotification(id);
    toast.dismiss(id);
    await HandleNotifications("delete", id);
  }

  const HandleSingOut = async () => {
    const canLeave = await requestLeave({ reason: "logout" });
    if (!canLeave) return;
    await smetaAxios.get("leave", {
      loadMask: true,
    });
    createCokies("drx", "");
    createCokies("LicG", "");
    updateOptionsForUser({ from: "" });
    RemoveAllBG();
    resetWorkArea();
    backToLoginPage();
  };

  const handleClickAway = () => {
    setAnim(false);
    setTimeout(() => {
      setIsOpen(false);
    }, 499);
  };

  const HeaderIcon = (props: React.SVGProps<SVGSVGElement> & { color?: string }) => (
    <div>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        className={clsx(styles.HeaderIcon)}
        fill="none"
        {...props}
      >
        <path d="M19.903 8.586a.997.997 0 0 0-.196-.293l-6-6a.997.997 0 0 0-.293-.196c-.03-.014-.062-.022-.094-.033a.991.991 0 0 0-.259-.051C13.04 2.011 13.021 2 13 2H6c-1.103 0-2 .897-2 2v16c0 1.103.897 2 2 2h12c1.103 0 2-.897 2-2V9c0-.021-.011-.04-.013-.062a.952.952 0 0 0-.051-.259c-.01-.032-.019-.063-.033-.093zM16.586 8H14V5.414L16.586 8zM6 20V4h6v5a1 1 0 0 0 1 1h5l.002 10H6z"></path>
        <path d="M8 12h8v2H8zm0 4h8v2H8zm0-8h2v2H8z"></path>
      </svg>
    </div>
  );

  return (
    <AppBar
      className={clsx(styles.AppBar)}
      elevation={0}
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, height: "inherit" }}
    >
      <Toolbar
        className={clsx(styles.Toolbar, {
          [styles.toolbarPadding0]: !isEmptyObject(headForm),
          [styles.toolbarPadding24]: isEmptyObject(headForm),
        })}
      >
        {!isEmptyObject(headForm) ? (
          <Suspense fallback={null}>
            <FormsMainFile head={true} jsonFormData={headForm} hideSectionTools style={{ overflow: "hidden" }} />
          </Suspense>
        ) : (
          <div className={clsx(styles.headerTitleIcon)}>
            <HeaderIcon className={clsx(styles.HeaderIcon)} />
            <Typography noWrap component="div" sx={{ mr: 2 }} className={clsx(styles.headerTitle)}>
              <div style={{ fontWeight: "bold" }}>
                {drx.current[2]} [{drx.current[0].replace(".drx", "")}]
              </div>
              <div>{` ${drx.current[1]}`}</div>
            </Typography>
          </div>
        )}

        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Уведомления">
          <IconButton id={"NotificationIcon"} onClick={toggleNotificationCenter}>
            <Badge badgeContent={unreadCount} color="primary">
              <NotificationsIcon
                className={clsx("iconButtonStimate", {
                  light: theme === "light",
                })}
              />
            </Badge>
          </IconButton>
        </Tooltip>
        <IconButton
          style={{
            display: window.location.origin === "http://localhost:3000" ? "" : "none",
          }}
          size="large"
          onClick={addNotification}
          id="HeadTestButton"
        >
          <InputIcon className={clsx("iconButtonStimate", { light: theme === "light" })} />
        </IconButton>
        <SupportButton />
        <UserProfile leave={HandleSingOut} />
      </Toolbar>
      <Popper
        onDoubleClick={() => {}}
        open={isOpen}
        anchorEl={anchorEl}
        className={clsx(styles.popperRoot)}
        // onResize={undefined}
        // onResizeCapture={undefined}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Grow in={anim} className={clsx(styles.popperGrow)} {...(isOpen ? { timeout: 500 } : {})}>
            <GridLegacy
              container
              direction="column"
              justifyContent="center"
              alignItems="center"
              className={clsx(styles.notifOuterGrid)}
            >
              <GridLegacy item className={clsx(styles.notifHeaderGrid)}>
                <Typography
                  variant="h5"
                  className={clsx("fontColorStimate", {
                    light: theme === "light",
                  })}
                  style={{ color: "var(--smeta-text-primary)" }}
                >
                  Центр уведомлений
                </Typography>
              </GridLegacy>
              <GridLegacy item className={clsx(styles.notifBodyGrid)}>
                <Stack className={styles.notifListWrap} spacing={2}>
                  {(!notifications.length || (unreadCount === 0 && showUnreadOnly)) && (
                    <GridLegacy item>
                      <h4 style={{ textAlign: "center" }}>У вас нет уведомлений!</h4>
                    </GridLegacy>
                  )}
                  {(showUnreadOnly ? notifications.filter((v) => !v.read) : notifications).map(
                    (notification, index) => {
                      return (
                        <Alert
                          key={`header-alert_${index}`}
                          severity={"info"}
                          action={
                            Number(notification.data.Options) & NotifyFlag_CanClose ? (
                              notification.read ? (
                                <CheckIcon />
                              ) : (
                                <Tooltip title="Завершить">
                                  <IconButton
                                    color="primary"
                                    aria-label="upload picture"
                                    component="span"
                                    onClick={() => markAsDone(notification.id)}
                                  >
                                    <MarkChatReadIcon />
                                  </IconButton>
                                </Tooltip>
                              )
                            ) : (
                              false
                            )
                          }
                        >
                          <AlertTitle>
                            {notification.data.Time} {notification.data.Title}
                          </AlertTitle>

                          {!isEmptyObject(notification.data.Command) ? (
                            <Link
                              onClick={() =>
                                HandleNotificationClick(
                                  notification.data.Command,
                                  notification.data.Options,
                                  notification.id,
                                )
                              }
                              style={{ cursor: "pointer" }}
                              underline="hover"
                            >
                              {notification.data.Content}
                            </Link>
                          ) : (
                            notification.data.Content
                          )}
                        </Alert>
                      );
                    },
                  )}
                </Stack>
              </GridLegacy>

              <GridLegacy item className={clsx(styles.notifFooterGrid, "styles.HeaderStimate")}>
                <Button
                  variant="outlined"
                  sx={updateNotificationsButton()}
                  className={clsx(styles.notifActionButton)}
                  value={"refresh"}
                  onClick={Notifications}
                >
                  Обновить
                </Button>
                <Button
                  variant="outlined"
                  sx={updateNotificationsButton()}
                  className={clsx(styles.notifActionButton)}
                  value={"refresh"}
                  onClick={finishAll}
                >
                  Прочитать всё
                </Button>
                {/* <FormGroup sx={{ color: "var(--font-color-light)" }}>
                  <FormControlLabel
                    control={
                      <Switch
                        onChange={toggleFilter}
                        checked={showUnreadOnly}
                      />
                    }
                    label="Показать только не завершенные"
                  />
                </FormGroup> */}
              </GridLegacy>
            </GridLegacy>
          </Grow>
        </ClickAwayListener>
      </Popper>
    </AppBar>
  );
};

export default DashboardNavbar;
